import { ColDef, ValueFormatterParams } from 'ag-grid-community';
import { PermissionService, StatusTagCellRendererComponent, User } from '@workspace-app/shared';
import { Bill } from './bill-data.service';
import { BillingComponent } from './billing.component';
import { BillingActionsCellRendererComponent } from './billing-actions-cell-renderer.component';

export const BILL_TYPE_LABELS: Record<string, string> = {
  DirectBill: 'Direct',
  GlBill: 'GL Collective',
  PowderDirectBill: 'Powder Direct',
  PowderGlBill: 'Powder GL',
  PretreatmentDirectBill: 'Pretreatment Direct',
  PretreatmentGlBill: 'Pretreatment GL',
};

export function getBillingColumns(
  caller: BillingComponent,
  user: User | undefined,
  permissionService: PermissionService
): ColDef[] {
  const columns: ColDef[] = [];

  const canAdmin = permissionService.hasEffectivePermission(user, 'Billing.Admin');
  const canRead = permissionService.hasEffectivePermission(user, 'Billing.Read');
  const canArchive = permissionService.hasEffectivePermission(user, 'Archive.Access');
  const canRestore = permissionService.hasEffectivePermission(user, 'Archive.Restore');

  if (canRead || canAdmin || canArchive || canRestore) {
    const actionContext: Record<string, unknown> = {};

    // Download PDF - only for issued or paid bills (view via row click)
    if (canRead || canAdmin) {
      actionContext['downloadFn'] = (bill: Bill) => caller.downloadPdf(bill);
      actionContext['downloadCondition'] = (bill: Bill) =>
        !bill.isArchived && (bill.status === 'Issued' || bill.status === 'Paid');
    }

    if (canAdmin) {
      // Issue - only for draft bills
      actionContext['issueFn'] = (bill: Bill) => caller.issueBill(bill);
      actionContext['issueCondition'] = (bill: Bill) => !bill.isArchived && bill.status === 'Draft';

      // Discard - only for draft bills
      actionContext['discardFn'] = (bill: Bill) => caller.discardDraft(bill);
      actionContext['discardCondition'] = (bill: Bill) => !bill.isArchived && bill.status === 'Draft';

      // Send email - only for issued bills
      actionContext['sendEmailFn'] = (bill: Bill) => caller.sendEmail(bill);
      actionContext['sendEmailCondition'] = (bill: Bill) => !bill.isArchived && bill.status === 'Issued';

      // Mark as paid - only for issued bills
      actionContext['markPaidFn'] = (bill: Bill) => caller.markAsPaid(bill);
      actionContext['markPaidCondition'] = (bill: Bill) => !bill.isArchived && bill.status === 'Issued';

      // Cancel - only for issued bills
      actionContext['cancelFn'] = (bill: Bill) => caller.cancelBill(bill);
      actionContext['cancelCondition'] = (bill: Bill) => !bill.isArchived && bill.status === 'Issued';

      // New version - for issued or cancelled bills (not paid)
      actionContext['newVersionFn'] = (bill: Bill) => caller.createNewVersion(bill);
      actionContext['newVersionCondition'] = (bill: Bill) =>
        !bill.isArchived && ['Issued', 'Cancelled'].includes(bill.status);
    }

    // Archive - for terminal state bills (paid/cancelled)
    if (canArchive) {
      actionContext['archiveFn'] = (bill: Bill) => caller.archiveBill(bill);
      actionContext['archiveCondition'] = (bill: Bill) =>
        !bill.isArchived && ['Paid', 'Cancelled'].includes(bill.status);
    }

    // Restore - for archived bills
    if (canRestore) {
      actionContext['restoreFn'] = (bill: Bill) => caller.restoreBill(bill);
      actionContext['restoreCondition'] = (bill: Bill) => bill.isArchived === true;
    }

    columns.push({
      headerName: 'Actions',
      cellRenderer: BillingActionsCellRendererComponent,
      cellRendererParams: {
        context: actionContext,
      },
      cellClass: 'action-buttons-cell',
      pinned: 'left',
      lockPosition: 'left',
      suppressMovable: true,
      minWidth: 150,
      width: 150,
      maxWidth: 200,
    });
  }

  columns.push(
    {
      field: 'completeBillNumber',
      headerName: 'Bill Number',
      sortable: true,
      floatingFilter: false,
      filter: 'agTextColumnFilter',
      filterParams: {
        maxNumConditions: 1,
        filterOptions: ['contains'],
        buttons: ['reset'],
      },
      minWidth: 100,
      width: 150,
      maxWidth: 200,
    },
    {
      field: 'billingYear',
      headerName: 'Year',
      sortable: true,
      floatingFilter: false,
      filter: 'agSetColumnFilter',
      filterParams: {
        values: caller.getAvailableYears,
        buttons: ['reset'],
      },
      minWidth: 100,
      width: 120,
      maxWidth: 140,
    },
    {
      field: 'billType',
      headerName: 'Type',
      sortable: true,
      floatingFilter: false,
      filter: 'agSetColumnFilter',
      valueFormatter: (params: ValueFormatterParams) => BILL_TYPE_LABELS[params.value] ?? params.value,
      filterParams: {
        values: Object.keys(BILL_TYPE_LABELS),
        buttons: ['reset'],
      },
      minWidth: 120,
      width: 140,
      maxWidth: 160,
    },
    {
      field: 'recipientOrganisation.name',
      headerName: 'Recipient',
      sortable: true,
      floatingFilter: false,
      filter: 'agTextColumnFilter',
      filterParams: {
        maxNumConditions: 1,
        filterOptions: ['contains'],
        buttons: ['reset'],
      },
      minWidth: 200,
      width: 280,
    },
    {
      field: 'totalAmount',
      headerName: 'Total Amount',
      sortable: true,
      floatingFilter: false,
      valueFormatter: (params: ValueFormatterParams) => {
        if (params.value == null) return '';
        const currency = params.data?.currency || 'EUR';
        return new Intl.NumberFormat('de-CH', {
          style: 'currency',
          currency: currency,
        }).format(params.value);
      },
      minWidth: 140,
      width: 160,
      maxWidth: 180,
    },
    {
      field: 'status',
      headerName: 'Status',
      sortable: true,
      floatingFilter: false,
      filter: 'agSetColumnFilter',
      cellRenderer: StatusTagCellRendererComponent,
      cellRendererParams: {
        statusColors: {
          Draft: 'secondary',
          Issued: 'info',
          Paid: 'success',
          Cancelled: 'danger',
        },
      },
      filterParams: {
        values: ['Draft', 'Issued', 'Paid', 'Cancelled'],
        buttons: ['reset'],
      },
      minWidth: 120,
      width: 140,
      maxWidth: 160,
    },
    {
      field: 'createdAt',
      headerName: 'Created',
      sortable: true,
      floatingFilter: false,
      filter: 'agDateColumnFilter',
      valueFormatter: (params: ValueFormatterParams) => {
        if (!params.value) return '';
        return new Date(params.value).toLocaleDateString('de-CH');
      },
      filterParams: {
        buttons: ['reset'],
      },
      minWidth: 120,
      width: 140,
      maxWidth: 160,
    },
    {
      field: 'issuedAt',
      headerName: 'Issued',
      sortable: true,
      floatingFilter: false,
      filter: 'agDateColumnFilter',
      valueFormatter: (params: ValueFormatterParams) => {
        if (!params.value) return '';
        return new Date(params.value).toLocaleDateString('de-CH');
      },
      filterParams: {
        buttons: ['reset'],
      },
      minWidth: 120,
      width: 140,
      maxWidth: 160,
    },
    {
      field: 'paidAt',
      headerName: 'Paid',
      sortable: true,
      floatingFilter: false,
      filter: 'agDateColumnFilter',
      valueFormatter: (params: ValueFormatterParams) => {
        if (!params.value) return '';
        return new Date(params.value).toLocaleDateString('de-CH');
      },
      filterParams: {
        buttons: ['reset'],
      },
      minWidth: 120,
      width: 140,
      maxWidth: 160,
    }
  );

  return columns;
}
